import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Trash2, Grid2X2, Ruler, X } from 'lucide-react';
import { useAppStore, useSelectedRooms } from '@/stores/appStore';
import { useRoomsStandardized } from '@/hooks/entities/useRoomsStandardized';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { queryKeys } from '@/lib/queryKeys';
import { logger } from '@/lib/logger';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface RoomDrawerV2Props {
  floorPlanId: string;
}

export function RoomDrawerV2({ floorPlanId }: RoomDrawerV2Props) {
  const activeDrawer = useAppStore(state => state.ui.activeDrawer);
  const setActiveDrawer = useAppStore(state => state.setActiveDrawer);
  const clearEntitySelection = useAppStore(state => state.clearEntitySelection);
  const setHoveredEntity = useAppStore(state => state.setHoveredEntity);
  const selectedRoomIds = useSelectedRooms();
  const showDimensions = useAppStore(state => state.rooms.showDimensions);
  const showArea = useAppStore(state => state.rooms.showArea);
  const setRoomDimensionsVisible = useAppStore(state => state.setRoomDimensionsVisible);
  const setRoomAreaVisible = useAppStore(state => state.setRoomAreaVisible);
  const {
    rooms: roomsData = [],
    updateRoom,
    deleteRoom,
    refetch,
  } = useRoomsStandardized(floorPlanId);
  const queryClient = useQueryClient();
  const { data } = useFloorPlanContext();

  const selectedRooms = useMemo(
    () => roomsData.filter(room => selectedRoomIds.has(room.id)),
    [roomsData, selectedRoomIds],
  );

  // Clean up selection if selected rooms no longer exist
  useEffect(() => {
    if (selectedRoomIds.size > 0 && selectedRooms.length === 0) {
      // All selected rooms have been deleted, clear the selection
      clearEntitySelection('room');
      setActiveDrawer(null);
    } else if (selectedRoomIds.size > selectedRooms.length) {
      // Some selected rooms have been deleted, update selection to only include existing rooms
      const existingRoomIds = new Set(selectedRooms.map(room => room.id));
      selectedRoomIds.forEach(id => {
        if (!existingRoomIds.has(id)) {
          useAppStore.getState().toggleEntitySelection('room', id);
        }
      });
    }
  }, [selectedRoomIds, selectedRooms, clearEntitySelection, setActiveDrawer]);

  const isOpen = activeDrawer === 'room' && selectedRooms.length > 0;
  const selectedCount = selectedRooms.length;
  const selectedRoom = selectedCount === 1 ? selectedRooms[0] : null;

  const [tempRoomNames, setTempRoomNames] = useState<Record<string, string>>({});
  const [isUpdating, setIsUpdating] = useState(false);
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Initialize room names when selection changes
  useEffect(() => {
    if (selectedRooms.length === 0) return;

    const names: Record<string, string> = {};
    selectedRooms.forEach(room => {
      names[room.id] = room.name || '';
    });
    setTempRoomNames(names);

    // Reset isUpdating state when selection changes
    // This ensures the drawer isn't stuck in updating state from previous operations
    setIsUpdating(false);

    // On touch devices, ensure no input gets focus when drawer opens
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice && isOpen) {
      // Small delay to ensure drawer is rendered
      setTimeout(() => {
        // Blur any active element to prevent keyboard from showing
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }, 50);
    }
  }, [selectedRooms, isOpen]);

  // Listen for focus room name event
  useEffect(() => {
    const handleFocusRoomName = (event: CustomEvent) => {
      const { roomId } = event.detail;
      if (selectedRooms.length === 1 && selectedRooms[0].id === roomId) {
        // Check if this is a touch device
        const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

        // On touch devices, don't auto-focus to prevent keyboard from popping up
        // User can tap the input field directly if they want to edit
        if (!isTouchDevice) {
          // Focus the input field only on desktop
          const input = inputRefs.current[roomId];
          if (input) {
            input.focus();
            input.select(); // Select all text for easy replacement
          }
        }
      }
    };

    window.addEventListener('focusRoomName', handleFocusRoomName as any);
    return () => {
      window.removeEventListener('focusRoomName', handleFocusRoomName as any);
    };
  }, [selectedRooms]);

  const handleSaveNames = useCallback(async () => {
    try {
      setIsUpdating(true);
      const updates = selectedRooms
        .filter(
          room => tempRoomNames[room.id] !== undefined && tempRoomNames[room.id] !== room.name,
        )
        .map(room => updateRoom(room.id, { name: tempRoomNames[room.id] }));

      if (updates.length > 0) {
        await Promise.all(updates);
        // Invalidate the rooms query to force immediate UI update
        await queryClient.invalidateQueries({ queryKey: queryKeys.rooms(floorPlanId) });
        toast.success('Room names updated');
      }
    } catch (error) {
      logger.error('Error updating room names:', error);
      toast.error('Failed to update room names');
    } finally {
      setIsUpdating(false);
    }
  }, [selectedRooms, tempRoomNames, updateRoom, queryClient, floorPlanId]);

  const handleClose = useCallback(async () => {
    // Save any pending changes before closing
    const hasChanges = selectedRooms.some(
      room => tempRoomNames[room.id] !== undefined && tempRoomNames[room.id] !== room.name,
    );

    if (hasChanges) {
      await handleSaveNames();
    }

    setActiveDrawer(null);
    clearEntitySelection('room');
    setHoveredEntity(null, null);
    setTempRoomNames({});
    setIsUpdating(false); // Reset updating state when closing
  }, [
    selectedRooms,
    tempRoomNames,
    handleSaveNames,
    setActiveDrawer,
    clearEntitySelection,
    setHoveredEntity,
  ]);

  const handleSheetInteraction = useCallback(
    async (e: any) => {
      // Save any pending changes when clicking outside
      const hasChanges = selectedRooms.some(
        room => tempRoomNames[room.id] !== undefined && tempRoomNames[room.id] !== room.name,
      );

      if (hasChanges) {
        await handleSaveNames();
      }
    },
    [selectedRooms, tempRoomNames, handleSaveNames],
  );

  const handleUpdateRoomName = async (roomId: string, newName: string) => {
    if (!selectedRooms.find(r => r.id === roomId)) return;

    try {
      setIsUpdating(true);
      await updateRoom(roomId, { name: newName });

      // Invalidate the rooms query to force immediate UI update
      await queryClient.invalidateQueries({ queryKey: queryKeys.rooms(floorPlanId) });

      toast.success('Room name updated');
    } catch (error) {
      logger.error('Error updating room name:', error);
      toast.error('Failed to update room name');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteRooms = async () => {
    try {
      setIsUpdating(true);

      // Store the room IDs before deletion
      const roomsToDelete = selectedRooms.map(room => room.id);
      const supabase = await getAuthenticatedSupabaseClient();

      // Before deleting rooms, update all fixtures, controls, and products that belong to these rooms
      // to set their room_id to null (No Room)
      const fixtureUpdates: Promise<any>[] = [];
      const controlUpdates: Promise<any>[] = [];
      const productUpdates: Promise<any>[] = [];

      // Get fixtures, controls, and lvProducts from the floor plan data
      const { fixtures, controls, lvProducts } = data;

      // Find fixtures that belong to rooms being deleted
      if (fixtures && fixtures.length > 0) {
        fixtures.forEach(fixture => {
          if (fixture.room_id && roomsToDelete.includes(fixture.room_id)) {
            fixtureUpdates.push(
              supabase.from('lighting_fixtures').update({ room_id: null }).eq('id', fixture.id),
            );
          }
        });
      }

      // Find controls that belong to rooms being deleted
      if (controls && controls.length > 0) {
        controls.forEach(control => {
          if (
            (control.room_id || control.roomId) &&
            roomsToDelete.includes(control.room_id || control.roomId)
          ) {
            controlUpdates.push(
              supabase.from('lighting_controls').update({ room_id: null }).eq('id', control.id),
            );
          }
        });
      }

      // Find LV products that belong to rooms being deleted
      if (lvProducts && lvProducts.length > 0) {
        lvProducts.forEach(product => {
          if (product.room_id && roomsToDelete.includes(product.room_id)) {
            productUpdates.push(
              supabase
                .from('lv_products')
                .update({ room_id: null })
                .eq('id', product.id)
                .then(result => {
                  if (result.error) {
                    logger.error('Error updating product room:', result.error);
                  }
                  return result;
                }),
            );
          }
        });
      }

      // Update all fixtures, controls, and products first
      if (fixtureUpdates.length > 0) {
        logger.log(`Updating ${fixtureUpdates.length} fixtures to remove room assignment`);
        await Promise.all(fixtureUpdates);
      }

      if (controlUpdates.length > 0) {
        logger.log(`Updating ${controlUpdates.length} controls to remove room assignment`);
        await Promise.all(controlUpdates);
      }

      if (productUpdates.length > 0) {
        logger.log(`Updating ${productUpdates.length} products to remove room assignment`);
        await Promise.all(productUpdates);
      }

      // Now delete the rooms
      await Promise.all(roomsToDelete.map(roomId => deleteRoom(roomId)));

      // Clear selection and close drawer BEFORE invalidating queries
      // This ensures the drawer closes immediately
      clearEntitySelection('room');
      setActiveDrawer(null);
      setHoveredEntity(null, null);
      setTempRoomNames({});
      setIsUpdating(false); // Reset updating state when closing

      // Invalidate all relevant queries to force a refetch
      await queryClient.invalidateQueries({ queryKey: queryKeys.rooms(floorPlanId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.fixtures(floorPlanId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.controls(floorPlanId) });
      await queryClient.invalidateQueries({
        queryKey: [...queryKeys.floorPlan(floorPlanId), 'lv-products'],
      });

      // Show toast with information about reassignments
      const totalReassigned = fixtureUpdates.length + controlUpdates.length + productUpdates.length;
      if (totalReassigned > 0) {
        toast.success(
          `Deleted ${roomsToDelete.length} room(s) and reassigned ${totalReassigned} element(s) to "No Room"`,
        );
      } else {
        toast.success(`Deleted ${roomsToDelete.length} room(s)`);
      }
    } catch (error) {
      logger.error('Error deleting rooms:', error);
      toast.error('Failed to delete rooms');
      setIsUpdating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, roomId: string) => {
    // Stop propagation to prevent global handlers (like space-to-pan) from intercepting
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      const newName = tempRoomNames[roomId];
      if (newName !== undefined && newName !== selectedRooms.find(r => r.id === roomId)?.name) {
        handleUpdateRoomName(roomId, newName);
      }
    }
  };

  const handleBlur = (roomId: string) => {
    const newName = tempRoomNames[roomId];
    const room = selectedRooms.find(r => r.id === roomId);
    if (room && newName !== undefined && newName !== room.name) {
      handleUpdateRoomName(roomId, newName);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Delete key to remove room
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // Only if not editing text
        if (document.activeElement?.tagName !== 'INPUT') {
          e.preventDefault();
          // Find delete button and click it to show confirmation dialog
          const deleteButton = document.querySelector(
            '[data-delete-room-button]',
          ) as HTMLButtonElement;
          if (deleteButton) {
            deleteButton.click();
          }
        }
      }
      // Escape to close drawer
      else if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  if (!isOpen || !selectedRoom) return null;

  // Only handle single room selection
  return (
    <Sheet open={isOpen} modal={false}>
      <SheetContent
        className="w-[400px] sm:w-[540px] flex flex-col h-full [&>button]:hidden"
        side="right"
        onPointerDownOutside={handleSheetInteraction}
        onInteractOutside={handleSheetInteraction}
      >
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2">
              <Grid2X2 className="h-5 w-5" />
              Room Details
            </SheetTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="h-8 w-8"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-6">
          {/* Room Details */}
          <div className="p-4 border rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Room Information</h4>
              {selectedRoomIds.has(selectedRoom.id) && (
                <Badge variant="secondary" className="text-xs">
                  Click vertices to edit shape
                </Badge>
              )}
            </div>

            <div className="space-y-3">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="room-name">Room Name</Label>
                <Input
                  ref={el => {
                    inputRefs.current[selectedRoom.id] = el;
                  }}
                  id="room-name"
                  value={tempRoomNames[selectedRoom.id] ?? ''}
                  onChange={e =>
                    setTempRoomNames(prev => ({ ...prev, [selectedRoom.id]: e.target.value }))
                  }
                  onKeyDown={e => handleKeyDown(e, selectedRoom.id)}
                  onKeyUp={e => e.stopPropagation()}
                  onBlur={() => handleBlur(selectedRoom.id)}
                  placeholder="Enter room name"
                  disabled={isUpdating}
                  // Never auto-focus this input
                  autoFocus={false}
                  // Prevent focus on touch to avoid keyboard popup
                  onFocus={e => {
                    // Check if this is a touch device and if focus wasn't triggered by direct user interaction
                    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
                    if (isTouchDevice && !e.isTrusted) {
                      // Blur immediately if this is an automatic focus on a touch device
                      e.currentTarget.blur();
                    }
                  }}
                  // Prevent auto-focus on touch devices when drawer opens
                  onTouchStart={e => {
                    // Only allow focus if user specifically taps on the input
                    // This prevents keyboard from opening when drawer opens
                    const input = e.currentTarget;
                    if (document.activeElement !== input) {
                      // Only prevent default if the event is cancelable (non-passive)
                      if (e.cancelable) {
                        e.preventDefault();
                      }
                    }
                  }}
                />
              </div>

              {/* Vertices */}
              {selectedRoom.points && (
                <div className="flex justify-between items-center">
                  <Label className="text-sm text-muted-foreground">Vertices</Label>
                  <span className="text-sm font-medium">{selectedRoom.points.length} points</span>
                </div>
              )}
            </div>
          </div>

          {/* Dimension Settings */}
          <div className="p-4 border rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Display Settings</h4>
            </div>

            <div className="space-y-3">
              {/* Show Dimensions Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Ruler className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="show-dimensions" className="text-sm">
                    Show Dimensions
                  </Label>
                </div>
                <Switch
                  id="show-dimensions"
                  checked={showDimensions}
                  onCheckedChange={setRoomDimensionsVisible}
                />
              </div>

              {/* Show Area Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Grid2X2 className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="show-area" className="text-sm">
                    Show Area
                  </Label>
                </div>
                <Switch id="show-area" checked={showArea} onCheckedChange={setRoomAreaVisible} />
              </div>
            </div>
          </div>

          {/* Editing Instructions */}
          <div className="p-4 border rounded-lg space-y-2 bg-muted/50">
            <h4 className="text-sm font-medium">Editing Instructions</h4>
            <div className="space-y-1 text-xs text-muted-foreground">
              <p>• Drag the room to move it</p>
              <p>• Drag white circles to reshape</p>
              <p>• Tap green circles to add vertices</p>
              <p>• Long-press white circles to delete (min 3)</p>
              <p className="text-orange-600 font-medium">• Changes save when you click outside</p>
            </div>
          </div>

          {/* Actions */}
          <div className="p-4 border rounded-lg space-y-4">
            <h4 className="text-sm font-medium">Actions</h4>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  className="w-full"
                  disabled={isUpdating}
                  data-delete-room-button
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Room
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this room?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete the room
                    {selectedRoom.name && ` "${selectedRoom.name}"`}.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteRooms}>Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
